import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkle, Image as ImageIcon, ChatCircleDots, PaperPlaneTilt, Megaphone, Copy, Trash, LinkedinLogo, FacebookLogo, InstagramLogo, TiktokLogo, TwitterLogo, ThumbsUp, ChatCircle, ShareFat, DotsThreeVertical, X, Heart, BookmarkSimple } from "@phosphor-icons/react";
import { BRAND_ASSETS, SOCIAL_ACCOUNTS } from "../lib/brand";
import { PageHeader } from "../components/ui/kaizen";

const PLATFORM_META = {
  linkedin: { label: "LinkedIn", icon: LinkedinLogo, color: "#0A66C2" },
  facebook: { label: "Facebook", icon: FacebookLogo, color: "#1877F2" },
  instagram: { label: "Instagram", icon: InstagramLogo, color: "#E4405F" },
  tiktok: { label: "TikTok / Reels", icon: TiktokLogo, color: "#0A0A0A" },
  twitter: { label: "X / Twitter", icon: TwitterLogo, color: "#0A0A0A" },
};

export default function MarketingModule() {
  const [tab, setTab] = useState("campaign");
  const [postForm, setPostForm] = useState({ platform: "instagram", topic: "", tone: "inspirant" });
  const [postOut, setPostOut] = useState("");
  const [imgPrompt, setImgPrompt] = useState("");
  const [img, setImg] = useState(null);
  const [images, setImages] = useState([]);
  const [posts, setPosts] = useState([]);
  const [avatarMsgs, setAvatarMsgs] = useState([]);
  const [avatarInput, setAvatarInput] = useState("");
  const [avatarSession, setAvatarSession] = useState(null);
  const [busy, setBusy] = useState(false);

  // Campaign state
  const [campaignForm, setCampaignForm] = useState({
    objective: "",
    offer: "",
    audience: "",
    cta: "",
    platforms: ["linkedin", "facebook", "instagram", "tiktok"],
  });
  const [campaign, setCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    api.get("/marketing/posts").then(r => setPosts(r.data)).catch(() => {});
    api.get("/marketing/images").then(r => setImages(r.data)).catch(() => {});
    api.get("/marketing/campaigns").then(r => setCampaigns(r.data)).catch(() => {});
  }, []);

  const genPost = async () => {
    setBusy(true); setPostOut("");
    try {
      const { data } = await api.post("/marketing/post", postForm);
      setPostOut(data.content);
      setPosts([data, ...posts]);
      toast.success("Post généré");
    } catch (e) { toast.error("Erreur"); } finally { setBusy(false); }
  };

  const genImage = async () => {
    if (!imgPrompt.trim()) return;
    setBusy(true); setImg(null);
    try {
      const { data } = await api.post("/marketing/image", { prompt: imgPrompt });
      setImg(data.image);
      setImages([data, ...images]);
      toast.success("Image générée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const chatAvatar = async () => {
    if (!avatarInput.trim()) return;
    const userMsg = avatarInput;
    setAvatarMsgs((m)=>[...m, { role: "user", text: userMsg }]);
    setAvatarInput(""); setBusy(true);
    try {
      const { data } = await api.post("/marketing/avatar-chat", { message: userMsg, session_id: avatarSession });
      setAvatarSession(data.session_id);
      setAvatarMsgs((m)=>[...m, { role: "avatar", text: data.reply }]);
    } catch (e) { toast.error("Erreur"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="marketing-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader title={<>Marketing & Réseaux sociaux</>} subtitle={<>Génère posts, images et discute avec ton avatar IA.</>} />
      </div>

      <div className="px-8 lg:px-12 py-8">
        <div className="flex gap-0 hard-border max-w-2xl mb-8 overflow-x-auto">
          {[["campaign","Campagne multi-plateformes"],["post","Post unique"],["image","Image IA"],["avatar","Avatar IA"]].map(([k,l]) => (
            <button key={k} data-testid={`mk-tab-${k}`} onClick={()=>setTab(k)} className={`flex-1 py-3 ticker transition-colors whitespace-nowrap px-4 ${tab===k?"bg-foreground text-white":"hover:bg-secondary"}`}>{l}</button>
          ))}
        </div>

        {tab === "campaign" && (
          <CampaignPanel
            form={campaignForm}
            setForm={setCampaignForm}
            campaign={campaign}
            setCampaign={setCampaign}
            campaigns={campaigns}
            setCampaigns={setCampaigns}
          />
        )}

        {tab === "post" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div>
                <label className="ticker text-muted-foreground block mb-2">Plateforme</label>
                <select data-testid="mk-platform" value={postForm.platform} onChange={(e)=>setPostForm({...postForm, platform:e.target.value})} className="w-full hard-border px-4 py-2.5 bg-white">
                  <option value="instagram">Instagram</option>
                  <option value="facebook">Facebook</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="twitter">X / Twitter</option>
                  <option value="tiktok">TikTok</option>
                </select>
              </div>
              <div>
                <label className="ticker text-muted-foreground block mb-2">Ton</label>
                <select data-testid="mk-tone" value={postForm.tone} onChange={(e)=>setPostForm({...postForm, tone:e.target.value})} className="w-full hard-border px-4 py-2.5 bg-white">
                  <option value="inspirant">Inspirant</option><option value="humoristique">Humoristique</option>
                  <option value="professionnel">Professionnel</option><option value="punchy">Punchy</option>
                </select>
              </div>
              <div>
                <label className="ticker text-muted-foreground block mb-2">Sujet du post</label>
                <textarea data-testid="mk-topic" rows={4} value={postForm.topic} onChange={(e)=>setPostForm({...postForm, topic:e.target.value})} className="w-full hard-border px-4 py-3 resize-y" placeholder="Ex: Annonce du Gala Circuit Québec le 16 mai…"/>
              </div>
              <button data-testid="mk-gen-post" onClick={genPost} disabled={busy} className="bg-[#D90429] hover:bg-[#BF0424] text-white px-6 py-3 ticker flex items-center gap-2 disabled:opacity-60 transition-colors">
                <Sparkle size={16}/> {busy?"Génération…":"Générer le post"}
              </button>
              {postOut && <pre data-testid="post-output" className="whitespace-pre-wrap hard-border p-5 text-sm bg-secondary/30">{postOut}</pre>}
            </div>
            <div>
              <div className="ticker text-muted-foreground mb-3">Historique des posts</div>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {posts.length === 0 && <div className="text-sm text-muted-foreground">Aucun post.</div>}
                {posts.map((p) => (
                  <div key={p.id} className="hard-border p-4">
                    <div className="ticker text-muted-foreground mb-1">{p.platform} · {p.tone}</div>
                    <div className="text-sm whitespace-pre-wrap line-clamp-5">{p.content}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "image" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <label className="ticker text-muted-foreground block mb-2">Décris l'image à générer</label>
              <textarea data-testid="mk-img-prompt" rows={4} value={imgPrompt} onChange={(e)=>setImgPrompt(e.target.value)} className="w-full hard-border px-4 py-3 resize-y" placeholder="Ex: Une athlète québécoise qui saute en hauteur au coucher du soleil, style cinématographique."/>
              <button data-testid="mk-gen-img" onClick={genImage} disabled={busy} className="mt-4 bg-[#D90429] hover:bg-[#BF0424] text-white px-6 py-3 ticker flex items-center gap-2 disabled:opacity-60 transition-colors">
                <ImageIcon size={16}/> {busy?"Création…":"Générer l'image"}
              </button>
              {img && <img src={img} alt="Generated" className="mt-6 hard-border w-full" data-testid="generated-image"/>}
            </div>
            <div>
              <div className="ticker text-muted-foreground mb-3">Galerie</div>
              <div className="grid grid-cols-2 gap-2 max-h-[500px] overflow-y-auto">
                {images.length === 0 && <div className="col-span-2 text-sm text-muted-foreground">Aucune image.</div>}
                {images.map((i) => <img key={i.id} src={i.image} alt={i.prompt} className="hard-border w-full h-40 object-cover"/>)}
              </div>
            </div>
          </div>
        )}

        {tab === "avatar" && (
          <div className="max-w-3xl">
            <div className="hard-border p-5 bg-secondary/30 flex items-start gap-3 mb-6">
              <ChatCircleDots size={22} className="text-[#D90429] mt-0.5"/>
              <div className="text-sm">
                Parle avec ton <strong>avatar IA</strong> — il répond comme toi. Pose-lui des questions comme tes clients le feraient : il t'aide à affiner ton message et ton positionnement.
              </div>
            </div>
            <div className="hard-border">
              <div className="hard-border-b px-5 py-3 bg-secondary/40 ticker">Conversation</div>
              <div className="h-[400px] overflow-y-auto p-5 space-y-3" data-testid="avatar-chat">
                {avatarMsgs.length === 0 && <div className="text-sm text-muted-foreground">Commence la conversation…</div>}
                {avatarMsgs.map((m, i) => (
                  <div key={i} className={`flex ${m.role==="user"?"justify-end":"justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2.5 text-sm ${m.role==="user"?"bg-foreground text-white":"hard-border bg-white"}`}>
                      <div className="ticker text-[10px] opacity-60 mb-1">{m.role==="user"?"Client":"Avatar"}</div>
                      {m.text}
                    </div>
                  </div>
                ))}
              </div>
              <div className="hard-border-t p-3 flex gap-2">
                <input data-testid="avatar-input" value={avatarInput} onChange={(e)=>setAvatarInput(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&chatAvatar()} placeholder="Écris un message…" className="flex-1 hard-border px-4 py-2.5"/>
                <button data-testid="avatar-send" onClick={chatAvatar} disabled={busy} className="bg-[#D90429] hover:bg-[#BF0424] text-white px-4 transition-colors disabled:opacity-60"><PaperPlaneTilt size={16}/></button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PublishButton({ provider, platform, content }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="ticker hover:text-[#D90429] flex items-center gap-1.5 transition-colors" data-testid={`publish-${platform || provider}`}>
        <PaperPlaneTilt size={14}/> Publier
      </button>
      {open && <PostPreviewModal provider={provider} platform={platform} content={content} onClose={() => setOpen(false)}/>}
    </>
  );
}

function PostPreviewModal({ provider, platform, content, onClose }) {
  const [busy, setBusy] = useState(false);
  const [mediaUrl, setMediaUrl] = useState("");
  const needsImage = provider === "meta" && platform === "instagram";
  const canSubmit = !needsImage || !!mediaUrl.trim();

  const doPublish = async () => {
    if (!canSubmit) {
      toast.error("Instagram exige une image (URL publique).");
      return;
    }
    setBusy(true);
    try {
      const body = { provider, content };
      if (platform) body.platform = platform;
      if (mediaUrl.trim()) body.media_url = mediaUrl.trim();
      const { data } = await api.post("/integrations/publish", body);
      if (data.status === "published") {
        toast.success(`Publié sur ${platform || provider} ! ID ${data.external_id || "ok"}`);
      } else {
        toast.success(data.note || "Publication enregistrée");
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Connecte d'abord ${provider} dans /app/integrations`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6 overflow-y-auto" data-testid="publish-preview-modal">
      <div className="bg-background hard-border max-w-lg w-full my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="hard-border-b px-5 py-4 flex items-center justify-between">
          <div>
            <div className="ticker text-muted-foreground">Aperçu avant publication</div>
            <div className="font-display font-bold text-lg">{(platform || provider).toUpperCase()}</div>
          </div>
          <button onClick={onClose} className="hover:text-[#D90429]" data-testid="preview-close"><X size={20}/></button>
        </div>

        <div className="p-5 bg-secondary/30">
          {platform === "instagram"
            ? <InstagramPreview content={content} mediaUrl={mediaUrl}/>
            : <FacebookPreview content={content} mediaUrl={mediaUrl}/>}
        </div>

        <div className="hard-border-t p-5 space-y-3">
          <label className="ticker text-muted-foreground block">
            URL de l'image {needsImage ? "(obligatoire pour Instagram)" : "(optionnel)"}
          </label>
          <input
            type="url"
            value={mediaUrl}
            onChange={(e) => setMediaUrl(e.target.value)}
            placeholder="https://... (JPEG/PNG public)"
            className="w-full hard-border px-3 py-2 font-mono text-xs"
            data-testid="preview-media-url"
          />
          <p className="text-xs text-muted-foreground">
            {needsImage
              ? "Instagram exige une image. L'URL doit être publiquement accessible par Meta (pas de localhost)."
              : "Tu peux publier juste du texte, ou ajouter une image pour plus d'impact."}
          </p>
        </div>

        <div className="hard-border-t p-5 flex items-center justify-between gap-3">
          <button onClick={onClose} className="ticker hover:text-[#D90429]" data-testid="preview-cancel">Annuler</button>
          <button
            onClick={doPublish}
            disabled={busy || !canSubmit}
            className="bg-[#D90429] hover:bg-[#BF0424] disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2.5 ticker flex items-center gap-2 transition-colors"
            data-testid="preview-confirm-publish"
          >
            <PaperPlaneTilt size={16}/> {busy ? "Envoi…" : `Publier sur ${platform || provider}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function FacebookPreview({ content, mediaUrl }) {
  return (
    <div className="bg-white rounded-md shadow-sm border border-[#dddfe2] overflow-hidden font-[system-ui,-apple-system,Segoe_UI,Roboto,sans-serif]" data-testid="fb-preview">
      <div className="flex items-start justify-between p-3">
        <div className="flex items-center gap-2.5">
          <img src={BRAND_ASSETS.DOJO_LOGO} alt="" className="w-10 h-10 rounded-full object-cover border border-[#dddfe2]"/>
          <div>
            <div className="text-[13px] font-semibold text-[#050505] leading-tight">{SOCIAL_ACCOUNTS.facebookPageName}</div>
            <div className="text-[11px] text-[#65676b] leading-tight flex items-center gap-1 mt-0.5">
              À l'instant · <span className="inline-block w-3 h-3 rounded-full bg-[#65676b]/20"/>
            </div>
          </div>
        </div>
        <DotsThreeVertical size={18} className="text-[#65676b]"/>
      </div>
      <div className="px-3 pb-2 text-[15px] text-[#050505] whitespace-pre-wrap leading-relaxed">
        {content || <span className="text-[#65676b] italic">(contenu vide)</span>}
      </div>
      {mediaUrl && <img src={mediaUrl} alt="" className="w-full max-h-80 object-cover border-y border-[#dddfe2]" onError={(e) => { e.currentTarget.style.display = "none"; }}/>}
      <div className="px-3 py-2 flex items-center justify-around border-t border-[#dddfe2] text-[#65676b] text-[13px]">
        <div className="flex items-center gap-1.5 px-3 py-1.5"><ThumbsUp size={16}/> J'aime</div>
        <div className="flex items-center gap-1.5 px-3 py-1.5"><ChatCircle size={16}/> Commenter</div>
        <div className="flex items-center gap-1.5 px-3 py-1.5"><ShareFat size={16}/> Partager</div>
      </div>
    </div>
  );
}

function InstagramPreview({ content, mediaUrl }) {
  return (
    <div className="bg-white border border-[#dbdbdb] rounded-md overflow-hidden font-[system-ui,-apple-system,Segoe_UI,Roboto,sans-serif] max-w-sm mx-auto" data-testid="ig-preview">
      <div className="flex items-center gap-2 p-3">
        <img src={BRAND_ASSETS.DOJO_LOGO} alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-offset-1 ring-[#E4405F]"/>
        <div className="flex-1">
          <div className="text-[13px] font-semibold text-[#262626]">{SOCIAL_ACCOUNTS.instagramHandle}</div>
        </div>
        <DotsThreeVertical size={18} className="text-[#262626]"/>
      </div>
      {mediaUrl ? (
        <img src={mediaUrl} alt="" className="w-full aspect-square object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
      ) : (
        <div className="w-full aspect-square bg-[#fafafa] flex items-center justify-center text-[#8e8e8e] text-xs border-y border-[#dbdbdb]">
          Image requise — colle l'URL ci-dessous
        </div>
      )}
      <div className="px-3 py-2 flex items-center gap-4 text-[#262626]">
        <Heart size={24}/>
        <ChatCircle size={24}/>
        <ShareFat size={24}/>
        <BookmarkSimple size={24} className="ml-auto"/>
      </div>
      <div className="px-3 pb-3 text-[13px] text-[#262626] leading-snug">
        <span className="font-semibold mr-1.5">{SOCIAL_ACCOUNTS.instagramHandle}</span>
        <span className="whitespace-pre-wrap">{content || <span className="text-[#8e8e8e] italic">(légende vide)</span>}</span>
      </div>
    </div>
  );
}

function CampaignPanel({ form, setForm, campaign, setCampaign, campaigns, setCampaigns }) {
  const [busy, setBusy] = useState(false);
  const PLATFORM_OPTIONS = ["linkedin", "facebook", "instagram", "tiktok", "twitter"];

  const togglePlatform = (p) => {
    setForm((f) => ({
      ...f,
      platforms: f.platforms.includes(p) ? f.platforms.filter(x => x !== p) : [...f.platforms, p],
    }));
  };

  const generate = async () => {
    if (!form.objective.trim() || !form.offer.trim() || form.platforms.length === 0) {
      toast.error("Objectif, offre et au moins 1 plateforme requis");
      return;
    }
    setBusy(true); setCampaign(null);
    try {
      const { data } = await api.post("/marketing/campaign", form);
      setCampaign(data);
      const fresh = await api.get("/marketing/campaigns");
      setCampaigns(fresh.data);
      toast.success(`Campagne générée pour ${form.platforms.length} plateforme${form.platforms.length>1?"s":""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const copyContent = (text) => { navigator.clipboard.writeText(text); toast.success("Copié"); };

  const delCampaign = async (id) => {
    await api.delete(`/marketing/campaigns/${id}`);
    if (campaign?.id === id) setCampaign(null);
    const fresh = await api.get("/marketing/campaigns");
    setCampaigns(fresh.data);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="campaign-panel">
      <div className="lg:col-span-5 space-y-4">
        <div className="hard-border p-5 bg-secondary/30 flex items-start gap-3">
          <Megaphone size={22} className="text-[#D90429] mt-0.5"/>
          <div className="text-sm">
            <strong>Campagne mère → déclinaisons par plateforme.</strong> Une seule offre, plusieurs versions adaptées au ton et au format de chaque réseau pro.
          </div>
        </div>
        <div>
          <label className="ticker text-muted-foreground block mb-2">Objectif de la campagne</label>
          <input data-testid="camp-objective" value={form.objective} onChange={(e)=>setForm({...form, objective:e.target.value})} className="w-full hard-border px-4 py-2.5" placeholder="Ex: Promouvoir le Gala Circuit Québec du 16 mai"/>
        </div>
        <div>
          <label className="ticker text-muted-foreground block mb-2">Offre / message principal</label>
          <textarea data-testid="camp-offer" rows={3} value={form.offer} onChange={(e)=>setForm({...form, offer:e.target.value})} className="w-full hard-border px-4 py-2.5 resize-y" placeholder="Ex: Soirée prestige rassemblant les meilleurs athlètes du Québec, billets VIP avec souper et combats de démonstration."/>
        </div>
        <div>
          <label className="ticker text-muted-foreground block mb-2">Audience cible (optionnel)</label>
          <input data-testid="camp-audience" value={form.audience} onChange={(e)=>setForm({...form, audience:e.target.value})} className="w-full hard-border px-4 py-2.5" placeholder="Ex: Parents 35-55, commanditaires, partenaires d'affaires"/>
        </div>
        <div>
          <label className="ticker text-muted-foreground block mb-2">Appel à l'action (CTA)</label>
          <input data-testid="camp-cta" value={form.cta} onChange={(e)=>setForm({...form, cta:e.target.value})} className="w-full hard-border px-4 py-2.5" placeholder="Ex: Réserver vos billets sur institutimpactelitemartial.com"/>
        </div>
        <div>
          <label className="ticker text-muted-foreground block mb-2">Plateformes (comptes pros uniquement)</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {PLATFORM_OPTIONS.map(p => {
              const m = PLATFORM_META[p];
              const Icon = m.icon;
              const active = form.platforms.includes(p);
              return (
                <button key={p} type="button" onClick={()=>togglePlatform(p)} data-testid={`camp-plat-${p}`}
                  className={`hard-border p-3 flex items-center gap-2 transition-colors ${active ? "bg-foreground text-white border-foreground" : "hover:bg-secondary"}`}>
                  <Icon size={18} weight={active ? "fill" : "regular"}/>
                  <span className="text-sm font-semibold">{m.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <button onClick={generate} disabled={busy} data-testid="camp-generate"
          className="w-full bg-[#D90429] hover:bg-[#BF0424] text-white py-3 ticker flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
          <Sparkle size={16}/> {busy ? `Génération de ${form.platforms.length} variantes…` : `Générer la campagne`}
        </button>
      </div>

      <div className="lg:col-span-7 space-y-4">
        {campaign && (
          <div data-testid="campaign-output" className="space-y-4">
            <div className="ticker text-muted-foreground">Variantes générées · {campaign.objective}</div>
            {Object.values(campaign.variants).map((v) => {
              const m = PLATFORM_META[v.platform];
              const Icon = m.icon;
              return (
                <div key={v.platform} className="hard-border" data-testid={`variant-${v.platform}`}>
                  <div className="hard-border-b px-5 py-3 bg-secondary/40 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon size={18} weight="fill" style={{ color: m.color }}/>
                      <span className="font-semibold text-sm">{v.label}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <button onClick={()=>copyContent(v.content)} className="ticker hover:text-[#D90429] flex items-center gap-1.5 transition-colors" data-testid={`copy-${v.platform}`}><Copy size={14}/> Copier</button>
                      <PublishButton
                        provider={v.platform === "tiktok" ? "tiktok" : v.platform === "linkedin" ? "linkedin" : v.platform === "facebook" || v.platform === "instagram" ? "meta" : "twitter"}
                        platform={v.platform}
                        content={v.content}
                      />
                    </div>
                  </div>
                  <pre className="whitespace-pre-wrap p-5 text-sm leading-relaxed font-[Satoshi]">{v.content}</pre>
                </div>
              );
            })}
          </div>
        )}
        {!campaign && (
          <div className="hard-border p-8 text-center bg-secondary/20" data-testid="campaign-empty">
            <Megaphone size={36} weight="duotone" className="mx-auto text-muted-foreground mb-3"/>
            <div className="font-display font-bold">Aucune campagne générée</div>
            <div className="text-sm text-muted-foreground mt-1">Remplis le formulaire à gauche et clique sur Générer.</div>
          </div>
        )}

        {campaigns.length > 0 && (
          <div>
            <div className="ticker text-muted-foreground mb-3 mt-8">Campagnes précédentes</div>
            <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="campaigns-history">
              {campaigns.map(c => (
                <div key={c.id} className="hard-border p-3 flex items-center justify-between hover:bg-secondary/30">
                  <button onClick={()=>setCampaign(c)} className="flex-1 text-left min-w-0">
                    <div className="text-sm font-semibold truncate">{c.objective}</div>
                    <div className="ticker text-muted-foreground">{(c.platforms || []).join(" · ")} · {new Date(c.created_at).toLocaleDateString("fr-CA")}</div>
                  </button>
                  <button onClick={()=>delCampaign(c.id)} className="text-muted-foreground hover:text-[#D90429] ml-2"><Trash size={14}/></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
